import { prefersReducedMotion } from "@theme/utilities";
import { ensureGsap } from "@theme/vp-motion";
//#region assets/vp-model-highlight.ts
/**
* Model highlight: gallery with GSAP entrance + background crossfades + brand toggle.
*
* Slide index is the single source of truth for thumb ↔ background pairing.
* In-flight crossfades are always cancelled and cleaned before a new sync.
*/
var REDUCED = () => prefersReducedMotion();
var ModelHighlight = class extends HTMLElement {
	/** @type {typeof window.gsap | null} */
	#gsap = null;
	/** @type {IntersectionObserver | null} */
	#io = null;
	/** @type {number} */
	#slideIndex = 0;
	/** @type {number} */
	#syncToken = 0;
	/** @type {boolean} */
	#entered = false;
	/** @type {AbortController | null} */
	#abort = null;
	/** @type {'primary' | 'secondary'} */
	#brand = "primary";
	connectedCallback() {
		this.#abort = new AbortController();
		const { signal } = this.#abort;
		this.#slideIndex = 0;
		this.#brand = this.dataset.mhBrand === "secondary" ? "secondary" : "primary";
		this.addEventListener("click", this.#onClick, { signal });
		this.addEventListener("keydown", this.#onKeydown, { signal });
		this.#boot();
	}
	disconnectedCallback() {
		this.#abort?.abort();
		this.#abort = null;
		this.#io?.disconnect();
		this.#io = null;
		this.#killPanelBgTweens(this.#activePanel());
	}
	/** @returns {HTMLElement | null} */
	#activePanel() {
		return this.querySelector(`[data-mh-panel="${this.#brand}"]`);
	}
	/**
	* @param {ParentNode | null} panel
	*/
	#killPanelBgTweens(panel) {
		const gsap = this.#gsap;
		if (!gsap || !panel) return;
		gsap.killTweensOf(panel.querySelectorAll("[data-mh-bg]"));
	}
	/**
	* Reset every bg in the panel so only `activeBg` is visible via CSS classes.
	* Clears leftover GSAP inline styles that can desync thumb vs background.
	*
	* @param {Element[]} bgs
	* @param {Element | null} activeBg
	*/
	#applyBgState(bgs, activeBg) {
		const gsap = this.#gsap;
		for (const bg of bgs) {
			const active = bg === activeBg;
			bg.classList.toggle("is-active", active);
			if (gsap) gsap.set(bg, { clearProps: "opacity,visibility,transform,zIndex" });
			else if (bg instanceof HTMLElement) {
				bg.style.removeProperty("opacity");
				bg.style.removeProperty("visibility");
				bg.style.removeProperty("transform");
				bg.style.removeProperty("z-index");
			}
		}
	}
	/**
	* @param {ParentNode} panel
	* @param {number} index
	* @returns {HTMLElement | null}
	*/
	#bgAt(panel, index) {
		const match = panel.querySelector(`[data-mh-bg][data-mh-slide-index="${index}"]`);
		if (match instanceof HTMLElement) return match;
		const fallback = panel.querySelector("[data-mh-bg]");
		return fallback instanceof HTMLElement ? fallback : null;
	}
	/**
	* Keep the selected thumbnail inside the clipped desktop gallery window.
	* Mobile uses the same scroll position so keyboard and arrow navigation agree.
	*
	* @param {ParentNode} panel
	* @param {Element | null} activeThumb
	* @param {boolean} instant
	*/
	#ensureThumbVisible(panel, activeThumb, instant) {
		const strip = panel.querySelector(".model-highlight__thumbs");
		if (!(strip instanceof HTMLElement) || !(activeThumb instanceof HTMLElement)) return;
		const visibleLeft = strip.scrollLeft;
		const visibleRight = visibleLeft + strip.clientWidth;
		const thumbLeft = activeThumb.offsetLeft;
		const thumbRight = thumbLeft + activeThumb.offsetWidth;
		let nextLeft = visibleLeft;
		if (thumbLeft < visibleLeft) nextLeft = thumbLeft;
		else if (thumbRight > visibleRight) nextLeft = thumbRight - strip.clientWidth;
		if (nextLeft === visibleLeft) return;
		strip.scrollTo({
			left: nextLeft,
			behavior: instant || REDUCED() ? "auto" : "smooth"
		});
	}
	async #boot() {
		this.#gsap = await ensureGsap();
		this.#syncUi({ instant: true });
		if (REDUCED() || !this.#gsap) {
			this.classList.add("is-ready");
			return;
		}
		this.#prepareEntrance();
		this.#io = new IntersectionObserver((entries) => {
			for (const entry of entries) if (entry.isIntersecting) {
				this.#playEntrance();
				this.#io?.disconnect();
				this.#io = null;
			}
		}, {
			threshold: .28,
			rootMargin: "0px 0px -8% 0px"
		});
		this.#io.observe(this);
	}
	#prepareEntrance() {
		const gsap = this.#gsap;
		if (!gsap) return;
		const panel = this.#activePanel();
		if (!panel) return;
		const copy = panel.querySelectorAll("[data-mh-enter]");
		const thumbs = panel.querySelectorAll("[data-mh-thumb]");
		const nav = panel.querySelectorAll("[data-mh-nav]");
		gsap.set(copy, {
			autoAlpha: 0,
			y: 20
		});
		gsap.set(thumbs, {
			autoAlpha: 0,
			y: 16
		});
		gsap.set(nav, { autoAlpha: 0 });
	}
	#playEntrance() {
		if (this.#entered) return;
		this.#entered = true;
		this.classList.add("is-ready");
		const gsap = this.#gsap;
		if (!gsap || REDUCED()) return;
		const panel = this.#activePanel();
		if (!panel) return;
		const copy = panel.querySelectorAll("[data-mh-enter]");
		const thumbs = panel.querySelectorAll("[data-mh-thumb]");
		const nav = panel.querySelectorAll("[data-mh-nav]");
		const bg = this.#bgAt(panel, this.#slideIndex);
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
		if (bg) {
			gsap.set(bg, { scale: 1.06 });
			tl.to(bg, {
				scale: 1,
				duration: 1.35,
				ease: "power1.out"
			}, 0);
		}
		tl.to(copy, {
			autoAlpha: 1,
			y: 0,
			duration: .65,
			stagger: .1
		}, .12);
		tl.to(thumbs, {
			autoAlpha: 1,
			y: 0,
			duration: .5,
			stagger: .06
		}, .35);
		tl.to(nav, {
			autoAlpha: 1,
			duration: .4
		}, .45);
	}
	/**
	* @param {MouseEvent} event
	*/
	#onClick = (event) => {
		const target = event.target;
		if (!(target instanceof Element)) return;
		const brandBtn = target.closest("[data-mh-brand-btn]");
		if (brandBtn instanceof HTMLElement) {
			const brand = brandBtn.dataset.mhBrandBtn;
			if (brand === "primary" || brand === "secondary") this.#setBrand(brand);
			return;
		}
		const thumb = target.closest("[data-mh-thumb]");
		if (thumb instanceof HTMLElement && this.#activePanel()?.contains(thumb)) {
			const index = Number(thumb.dataset.mhThumb);
			if (!Number.isNaN(index)) this.#setSlide(index);
			return;
		}
		const nav = target.closest("[data-mh-nav]");
		if (nav instanceof HTMLElement && this.#activePanel()?.contains(nav)) {
			const dir = nav.dataset.mhNav === "prev" ? -1 : 1;
			this.#stepSlide(dir);
		}
	};
	/**
	* @param {KeyboardEvent} event
	*/
	#onKeydown = (event) => {
		if (event.target instanceof Element ? event.target.closest("[data-mh-brand-btn]") : null) {
			if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
				event.preventDefault();
				const next = event.key === "ArrowLeft" ? "primary" : "secondary";
				this.#setBrand(next);
				this.querySelector(`[data-mh-brand-btn="${next}"]`)?.focus();
			}
			return;
		}
		if (event.key === "ArrowLeft") {
			event.preventDefault();
			this.#stepSlide(-1);
		} else if (event.key === "ArrowRight") {
			event.preventDefault();
			this.#stepSlide(1);
		}
	};
	/**
	* @param {'primary' | 'secondary'} brand
	*/
	#setBrand(brand) {
		if (brand === this.#brand) return;
		if (!this.querySelector(`[data-mh-panel="${brand}"]`)) return;
		this.#killPanelBgTweens(this.#activePanel());
		this.#brand = brand;
		this.dataset.mhBrand = brand;
		this.#slideIndex = 0;
		this.querySelectorAll("[data-mh-brand-btn]").forEach((btn) => {
			const active = btn.getAttribute("data-mh-brand-btn") === brand;
			btn.classList.toggle("is-active", active);
			btn.setAttribute("aria-selected", active ? "true" : "false");
			btn.setAttribute("tabindex", active ? "0" : "-1");
		});
		this.querySelectorAll("[data-mh-panel-host]").forEach((host) => {
			const active = host.getAttribute("data-mh-panel-host") === brand;
			host.toggleAttribute("hidden", !active);
		});
		this.querySelectorAll("[data-mh-panel]").forEach((panel) => {
			const active = panel.getAttribute("data-mh-panel") === brand;
			panel.classList.toggle("is-active", active);
			panel.toggleAttribute("hidden", !active);
		});
		this.#syncUi({ instant: true });
		const gsap = this.#gsap;
		const panel = this.#activePanel();
		if (gsap && panel && !REDUCED()) {
			const copy = panel.querySelectorAll("[data-mh-enter]");
			const thumbs = panel.querySelectorAll("[data-mh-thumb]");
			gsap.fromTo(copy, {
				autoAlpha: 0,
				y: 12
			}, {
				autoAlpha: 1,
				y: 0,
				duration: .45,
				stagger: .06,
				ease: "power2.out"
			});
			gsap.fromTo(thumbs, {
				autoAlpha: 0,
				y: 10
			}, {
				autoAlpha: 1,
				y: 0,
				duration: .4,
				stagger: .04,
				ease: "power2.out",
				delay: .08
			});
		}
	}
	/**
	* @param {number} index
	*/
	#setSlide(index) {
		if (index === this.#slideIndex) return;
		this.#slideIndex = index;
		this.#syncUi({ animateBg: true });
	}
	/**
	* @param {number} dir
	*/
	#stepSlide(dir) {
		const panel = this.#activePanel();
		if (!panel) return;
		const count = panel.querySelectorAll("[data-mh-thumb]").length;
		if (count < 1) return;
		const next = (this.#slideIndex + dir + count) % count;
		this.#setSlide(next);
	}
	/**
	* @param {{ instant?: boolean, animateBg?: boolean }} [opts]
	*/
	#syncUi(opts = {}) {
		const { instant = false, animateBg = !instant } = opts;
		const gsap = this.#gsap;
		const panel = this.#activePanel();
		if (!panel) return;
		const token = ++this.#syncToken;
		const targetIndex = this.#slideIndex;
		let activeThumb = null;
		panel.querySelectorAll("[data-mh-thumb]").forEach((thumb) => {
			const active = Number(thumb.dataset.mhThumb) === targetIndex;
			thumb.classList.toggle("is-active", active);
			thumb.setAttribute("aria-current", active ? "true" : "false");
			if (active) activeThumb = thumb;
		});
		this.#ensureThumbVisible(panel, activeThumb, instant);
		const bgs = Array.from(panel.querySelectorAll("[data-mh-bg]"));
		const nextBg = this.#bgAt(panel, targetIndex);
		if (!nextBg) return;
		const currentBg = bgs.find((bg) => bg.classList.contains("is-active") && bg !== nextBg) || null;
		this.#killPanelBgTweens(panel);
		if (!animateBg || !gsap || REDUCED() || !currentBg || currentBg === nextBg) {
			this.#applyBgState(bgs, nextBg);
			return;
		}
		for (const bg of bgs) {
			if (bg === currentBg || bg === nextBg) continue;
			bg.classList.remove("is-active");
			gsap.set(bg, { clearProps: "opacity,visibility,transform,zIndex" });
		}
		gsap.set(nextBg, {
			autoAlpha: 0,
			scale: 1.04,
			zIndex: 2
		});
		gsap.set(currentBg, {
			autoAlpha: 1,
			zIndex: 1
		});
		nextBg.classList.add("is-active");
		gsap.timeline({ onComplete: () => {
			if (this.#syncToken !== token || this.#slideIndex !== targetIndex) return;
			this.#applyBgState(bgs, nextBg);
		} }).to(nextBg, {
			autoAlpha: 1,
			scale: 1,
			duration: .85,
			ease: "power2.out"
		}, 0).to(currentBg, {
			autoAlpha: 0,
			duration: .7,
			ease: "power1.inOut"
		}, .05);
	}
};
if (!customElements.get("model-highlight")) customElements.define("model-highlight", ModelHighlight);
//#endregion
